import axios, { HttpStatusCode } from 'axios';
import { USER_ERROR_MESSAGES } from '../../corelogic/domain/errors/gateway.errorMessages/user.error.messages';
import {
  UserConflictError,
  UserCreationError,
  UserDeletionError,
  UserForbiddenError,
  UserNotFoundError,
  UserRetrievalError,
  UserServerError,
  UserUnauthorizedError,
  UserUpdateError,
} from '../../corelogic/domain/errors/user.error';
import {
  SignUpProfile,
  UserProfile,
} from '../../corelogic/domain/models/user.model';
import { UserGateway } from '../../corelogic/domain/ports/user.port';
import {
  sanitizeSignUpProfile,
  sanitizeUserProfileUpdate,
} from '../../corelogic/domain/utils/inputSanitization.utils';
import { withRateLimit } from '../../corelogic/domain/utils/rateLimiting.utils';
import { getSecureRequestHeaders } from '../../corelogic/domain/utils/securityHeaders.utils';
import { handleAuthError } from './auth.error.handler';

export class APIUserGateway implements UserGateway {
  private apiEndpoint: string;

  private handleUserError(
    error: unknown,
    operation: 'create' | 'get' | 'update' | 'delete',
  ): never {
    const badRequestErrorMap: Record<typeof operation, Error> = {
      create: new UserCreationError(USER_ERROR_MESSAGES.INVALID_DATA),
      get: new UserRetrievalError(USER_ERROR_MESSAGES.INVALID_REQUEST),
      update: new UserUpdateError(USER_ERROR_MESSAGES.INVALID_DATA),
      delete: new UserDeletionError(USER_ERROR_MESSAGES.INVALID_REQUEST),
    };

    if (!axios.isAxiosError(error)) {
      throw new UserServerError(USER_ERROR_MESSAGES.SERVER_ERROR);
    }

    // Check if error is auth-related
    if (error.response?.data?.error?.includes('token')) {
      throw handleAuthError(error);
    }

    switch (error.response?.status) {
      case HttpStatusCode.BadRequest:
        throw badRequestErrorMap[operation];
      case HttpStatusCode.Unauthorized:
        throw new UserUnauthorizedError(USER_ERROR_MESSAGES.UNAUTHORIZED);
      case HttpStatusCode.Forbidden:
        throw new UserForbiddenError(USER_ERROR_MESSAGES.FORBIDDEN);
      case HttpStatusCode.NotFound:
        throw new UserNotFoundError(USER_ERROR_MESSAGES.NOT_FOUND);
      case HttpStatusCode.Conflict:
        throw new UserConflictError(USER_ERROR_MESSAGES.ALREADY_EXISTS);
      case HttpStatusCode.InternalServerError:
        throw new UserServerError(USER_ERROR_MESSAGES.SERVER_ERROR);
      default:
        throw new UserServerError(USER_ERROR_MESSAGES.SERVER_ERROR);
    }
  }

  constructor(apiEndpoint: string) {
    this.apiEndpoint = apiEndpoint;
  }

  createUserProfile = withRateLimit(
    async (
      token: string,
      signUpProfile: SignUpProfile,
    ): Promise<UserProfile> => {
      try {
        // Sanitize input data before sending
        const sanitizedProfile = sanitizeSignUpProfile(signUpProfile);

        const response = await axios.post<{ user: UserProfile }>(
          `${this.apiEndpoint}/users`,
          { ...sanitizedProfile },
          {
            headers: {
              'Content-Type': 'application/json',
              ...getSecureRequestHeaders(token),
            },
          },
        );
        return response.data.user;
      } catch (error) {
        throw this.handleUserError(error, 'create');
      }
    },
    'highRisk', // 🟠 5/min - Account creation is high risk
    'user-create',
  );

  // doesn't need rate limiting because it's a read-only operation
  async getUserProfile(token: string): Promise<UserProfile> {
    try {
      const response = await axios.get<{ user: UserProfile }>(
        `${this.apiEndpoint}/users/me`,
        {
          headers: {
            ...getSecureRequestHeaders(token),
          },
        },
      );
      return response.data.user;
    } catch (error) {
      throw this.handleUserError(error, 'get');
    }
  }

  updateUserProfile = withRateLimit(
    async (
      token: string,
      updateProfile: Partial<UserProfile>,
    ): Promise<UserProfile> => {
      try {
        // Sanitize input data before sending
        const sanitizedProfile = sanitizeUserProfileUpdate(updateProfile);

        const response = await axios.patch<{ user: UserProfile }>(
          `${this.apiEndpoint}/users`,
          { ...sanitizedProfile },
          {
            headers: {
              'Content-Type': 'application/json',
              ...getSecureRequestHeaders(token),
            },
          },
        );
        return response.data.user;
      } catch (error) {
        throw this.handleUserError(error, 'update');
      }
    },
    'highRisk', // 🟠 5/min - Profile updates are high risk
    'user-update',
  );

  deleteUserProfile = withRateLimit(
    async (token: string): Promise<void> => {
      try {
        await axios.delete(`${this.apiEndpoint}/users`, {
          headers: {
            ...getSecureRequestHeaders(token),
          },
        });
      } catch (error) {
        throw this.handleUserError(error, 'delete');
      }
    },
    'criticalOperations', // 🔴 3/min - Account deletion is CRITICAL
    'user-delete',
    'Too many delete attempts. Please wait before trying to delete your account again.',
  );
}
